import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import UpgradeModal from '../components/UpgradeModal';
import QuotaDisplay from '../components/QuotaDisplay';
import QuotaUpgradeModal from '../components/QuotaUpgradeModal';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Users, TrendingUp, Upload, FileText, Edit, Eye, Lock, ChevronRight, GitBranch, Calendar, MessageSquare, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FounderDashboard() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [milestoneStats, setMilestoneStats] = useState({ total: 0, by_state: {}, scheduled_sessions: 0, next_session: null });
  const [profileViews, setProfileViews] = useState({ viewers: [], viewed: [] });
  const [uploading, setUploading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const { upgradeModal, checkFeature, closeUpgradeModal } = useFeatureAccess();

  // Theme-aware classes
  const bg = isDark ? 'bg-slate-900' : 'bg-slate-100';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-blue-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-blue-700';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-600';
  const badgeBg = isDark ? 'bg-blue-900/60 text-blue-200 border border-blue-700/50' : 'bg-blue-100 text-blue-800';
  const hoverCard = isDark ? 'hover:border-blue-400' : 'hover:border-blue-500';
  const innerCardBg = isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200';
  const verifiedColor = isDark ? 'text-green-400' : 'text-green-600';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, matchesRes, recsRes, milestoneStatsRes] = await Promise.all([
        axios.get(`${API}/profile/my-profile`),
        axios.get(`${API}/matches`),
        axios.get(`${API}/discovery/recommendations`),
        axios.get(`${API}/milestones/summary/stats`)
      ]);

      if (profileRes.data.exists) {
        setProfile(profileRes.data.profile);
      }
      setMatches(matchesRes.data);
      setRecommendations(recsRes.data.slice(0, 5));
      setMilestoneStats(milestoneStatsRes.data);

      // Fetch profile views if Pro user
      if (user?.plan_status !== 'free') {
        try {
          const viewsRes = await axios.get(`${API}/profile/views`);
          setProfileViews(viewsRes.data);
        } catch (e) {
          // Endpoint may not exist yet, ignore
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handlePitchDeckUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const hasAccess = await checkFeature('pitch_deck_upload');
    if (!hasAccess) {
      e.target.value = '';
      return;
    }

    if (!file.name.endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/profile/founder/upload-pitch-deck`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Pitch deck uploaded successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to upload pitch deck');
    } finally {
      setUploading(false);
    }
  };

  const getProfileCompleteness = () => {
    if (!profile) return { score: 0, breakdown: [], nextSteps: [] };
    let score = 0;
    const totalFields = 21; // Updated total fields
    const breakdown = [];
    const nextSteps = [];
    
    // Core fields (10 fields)
    if (profile.country) score++; else nextSteps.push('Add country');
    if (profile.city) score++; else nextSteps.push('Add city');
    if (profile.startup_name) score++;
    if (profile.industries?.length > 0) score++; else nextSteps.push('Select industries');
    if (profile.stage) score++;
    if (profile.one_line_pitch) score++; else nextSteps.push('Write one-line pitch');
    if (profile.problem) score++;
    if (profile.solution) score++;
    if (profile.target_customer) score++;
    if (profile.use_of_funds) score++;
    
    const coreScore = [profile.country, profile.city, profile.startup_name, profile.industries?.length > 0, profile.stage, profile.one_line_pitch, profile.problem, profile.solution, profile.target_customer, profile.use_of_funds].filter(Boolean).length;
    breakdown.push({ label: 'Core Info', score: coreScore, total: 10 });
    
    // Investment Alignment fields (6 fields)
    if (profile.primary_investor_type_sought?.length > 0) score++; else nextSteps.push('Add investor type sought');
    if (profile.previous_funding) score++;
    if (profile.target_round_structure) score++;
    if (profile.founder_ownership) score++; else nextSteps.push('Add founder ownership preference');
    if (profile.growth_ambition) score++; else nextSteps.push('Add growth ambition');
    if (profile.exit_vision) score++; else nextSteps.push('Add exit vision');
    
    const alignmentScore = [profile.primary_investor_type_sought?.length > 0, profile.previous_funding, profile.target_round_structure, profile.founder_ownership, profile.growth_ambition, profile.exit_vision].filter(Boolean).length;
    breakdown.push({ label: 'Investment Alignment', score: alignmentScore, total: 6 });
    
    // Capital Strategy fields (3 fields - new)
    if (profile.last_round_type) score++; else nextSteps.push('Add last round type');
    if (profile.current_raise_type) score++; else nextSteps.push('Add current raise type');
    if (profile.target_investor_type) score++; else nextSteps.push('Add target investor type');
    
    const capitalScore = [profile.last_round_type, profile.current_raise_type, profile.target_investor_type].filter(Boolean).length;
    breakdown.push({ label: 'Capital Strategy', score: capitalScore, total: 3 });
    
    // Bonus fields (2 fields)
    if (profile.pitch_deck_filename) score++;
    if (profile.traction) score++;
    
    const bonusScore = [profile.pitch_deck_filename, profile.traction].filter(Boolean).length;
    breakdown.push({ label: 'Bonus', score: bonusScore, total: 2 });
    
    return {
      score: Math.round((score / totalFields) * 100),
      breakdown,
      nextSteps: nextSteps.slice(0, 3) // Top 3 suggestions
    };
  };

  const completenessData = getProfileCompleteness();

  const isPro = user?.plan_status !== 'free';

  // Card click handlers with tier gating
  const handleCardClick = (cardType) => {
    switch (cardType) {
      case 'completeness':
        navigate('/profile-setup');
        break;
      case 'matches':
        navigate('/matches');
        break;
      case 'relationships':
        navigate('/relationships');
        break;
      case 'viewers':
        if (isPro) {
          navigate('/profile-views');
        } else {
          setUpgradeFeature('Profile Views Analytics');
          setShowUpgradeModal(true);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className={`min-h-screen ${bg}`} data-testid="founder-dashboard">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-3xl sm:text-4xl font-bold mb-2 ${textPrimary}`}>Founder Dashboard</h1>
            <p className={textSecondary}>Welcome back! Here's your startup overview.</p>
          </div>
          {profile && (
            <div className="flex gap-3">
              <Link to="/profile-setup">
                <Button variant="outline" className={`border ${isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`} data-testid="edit-profile-btn">
                  <Edit size={16} className="mr-2" />
                  Edit Profile
                </Button>
              </Link>
              <Link to={`/profile/${user?.user_id}?preview=investor`}>
                <Button className="btn-primary" data-testid="preview-profile-btn">
                  <Eye size={16} className="mr-2" />
                  Preview as Investor
                </Button>
              </Link>
            </div>
          )}
        </div>

        {!profile ? (
          <Card className={`shadow-sm p-8 mb-8 ${cardBg}`}>
            <div className="text-center">
              <h2 className={`text-2xl font-bold mb-4 ${textPrimary}`}>Complete Your Profile</h2>
              <p className={`mb-6 ${textSecondary}`}>Set up your founder profile to start connecting with investors</p>
              <Link to="/profile-setup">
                <Button className="btn-primary" data-testid="setup-profile-btn">Set Up Profile</Button>
              </Link>
            </div>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {/* Profile Completeness Card - clickable */}
              <Card 
                className={`shadow-sm p-6 cursor-pointer transition-all ${cardBg} ${hoverCard} hover:shadow-md`}
                onClick={() => handleCardClick('completeness')}
                data-testid="card-completeness"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${textSecondary}`}>Profile Completeness</span>
                  <TrendingUp className="text-blue-500" size={20} />
                </div>
                <div className={`text-3xl font-bold mb-2 ${textPrimary}`}>{completenessData.score}%</div>
                <Progress value={completenessData.score} className="h-2" />
                {completenessData.nextSteps?.length > 0 && (
                  <div className={`mt-2 text-xs ${textMuted}`}>
                    <span className="text-amber-500">Next: {completenessData.nextSteps[0]}</span>
                  </div>
                )}
                <div className={`flex items-center mt-1 text-xs ${textMuted}`}>
                  <span>Click to edit profile</span>
                  <ChevronRight size={14} className="ml-1" />
                </div>
              </Card>

              {/* Matches Card - clickable - shows lifetime stats */}
              <Card 
                className={`shadow-sm p-6 cursor-pointer transition-all ${cardBg} ${hoverCard} hover:shadow-md`}
                onClick={() => handleCardClick('matches')}
                data-testid="card-matches"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${textSecondary}`}>Likes / Matches</span>
                  <Users className="text-blue-500" size={20} />
                </div>
                <div className={`text-3xl font-bold ${textPrimary}`}>{matches.length}</div>
                <div className="flex items-center gap-3 mt-2">
                  {milestoneStats.lifetime?.total_matched > 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                      {milestoneStats.lifetime.total_matched} lifetime
                    </span>
                  )}
                </div>
                <div className={`flex items-center mt-1 text-xs ${textMuted}`}>
                  <span>View all matches</span>
                  <ChevronRight size={14} className="ml-1" />
                </div>
              </Card>

              {/* Relationship Progress Card - shows lifetime stats */}
              <Card 
                className={`shadow-sm p-6 cursor-pointer transition-all ${cardBg} ${hoverCard} hover:shadow-md`}
                onClick={() => handleCardClick('relationships')}
                data-testid="card-relationships"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${textSecondary}`}>Relationship Progress</span>
                  <GitBranch className="text-blue-500" size={20} />
                </div>
                <div className={`text-3xl font-bold ${textPrimary}`}>{milestoneStats.total}</div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {milestoneStats.lifetime?.total_pitch_proposed > 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                      {milestoneStats.lifetime.total_pitch_proposed} pitched
                    </span>
                  )}
                  {milestoneStats.lifetime?.total_meetings_scheduled > 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'}`}>
                      {milestoneStats.lifetime.total_meetings_scheduled} meetings
                    </span>
                  )}
                  {milestoneStats.scheduled_sessions > 0 && (
                    <span className={`text-xs flex items-center gap-1 ${textMuted}`}>
                      <Calendar size={12} />
                      {milestoneStats.scheduled_sessions} upcoming
                    </span>
                  )}
                </div>
                <div className={`flex items-center mt-1 text-xs ${textMuted}`}>
                  <span>Track milestones</span>
                  <ChevronRight size={14} className="ml-1" />
                </div>
              </Card>

              {/* Investors Viewing Card - tier gated */}
              <Card 
                className={`shadow-sm p-6 cursor-pointer transition-all ${cardBg} ${isPro ? `${hoverCard} hover:shadow-md` : 'opacity-80'}`}
                onClick={() => handleCardClick('viewers')}
                data-testid="card-viewers"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${textSecondary}`}>Investors Viewing</span>
                  <div className="flex items-center gap-1">
                    {!isPro && <Lock className="text-slate-400" size={14} />}
                    <FileText className="text-blue-500" size={20} />
                  </div>
                </div>
                <div className={`text-3xl font-bold ${textPrimary}`}>{isPro ? (profileViews.viewers?.length || recommendations.length) : recommendations.length}</div>
                <div className={`flex items-center mt-2 text-xs ${textMuted}`}>
                  {isPro ? (
                    <>
                      <span>View analytics</span>
                      <ChevronRight size={14} className="ml-1" />
                    </>
                  ) : (
                    <span className="text-amber-500">Pro feature</span>
                  )}
                </div>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Quota Display Card */}
              <QuotaDisplay mode="full" showUpgrade={true} />
              
              <Card className={`shadow-sm p-6 ${cardBg}`}>
                <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>Pitch Deck</h2>
                {profile.pitch_deck_filename ? (
                  <div className={`flex items-center gap-4 p-4 rounded-md border ${isDark ? 'bg-slate-600 border-slate-500' : 'bg-slate-50 border-slate-200'}`}>
                    <FileText className="text-blue-500" size={32} />
                    <div className="flex-1">
                      <p className={`font-medium ${textPrimary}`}>Pitch Deck Uploaded</p>
                      <p className={`text-sm ${textSecondary}`}>{profile.pitch_deck_filename}</p>
                    </div>
                    <label htmlFor="pitch-deck-upload">
                      <Button className="btn-secondary" data-testid="upload-pitch-deck" disabled={uploading}>
                        Replace
                      </Button>
                    </label>
                  </div>
                ) : (
                  <div className={`text-center py-8 rounded-md border ${isDark ? 'bg-slate-600 border-slate-500' : 'bg-slate-50 border-slate-200'}`}>
                    <Upload className="text-blue-500 mx-auto mb-4" size={48} />
                    <p className={`mb-4 ${textSecondary}`}>Upload your pitch deck (PDF)</p>
                    <label htmlFor="pitch-deck-upload">
                      <Button className="btn-primary" data-testid="upload-pitch-deck" disabled={uploading}>
                        {uploading ? 'Uploading...' : 'Upload Pitch Deck'}
                      </Button>
                    </label>
                  </div>
                )}
                <input
                  id="pitch-deck-upload"
                  type="file"
                  accept=".pdf"
                  onChange={handlePitchDeckUpload}
                  className="hidden"
                />
              </Card>

              <Card className={`shadow-sm p-6 ${cardBg}`}>
                <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>Startup Info</h2>
                <div className="space-y-3">
                  <div>
                    <span className={`text-sm font-medium ${textSecondary}`}>Startup Name</span>
                    <p className={`font-medium ${textPrimary}`}>{profile.startup_name}</p>
                  </div>
                  <div>
                    <span className={`text-sm font-medium ${textSecondary}`}>Stage</span>
                    <p className={textPrimary}>{profile.stage}</p>
                  </div>
                  <div>
                    <span className={`text-sm font-medium ${textSecondary}`}>Funding Asked</span>
                    <p className={textPrimary}>
                      {profile.currency} {profile.funding_asked_min.toLocaleString()} - {profile.funding_asked_max.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className={`text-sm font-medium ${textSecondary}`}>Industries</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {profile.industries.map(ind => (
                        <span key={ind} className={`text-xs px-2 py-1 rounded font-medium ${badgeBg}`}>
                          {ind}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            <Card className={`shadow-sm p-6 ${cardBg}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <h2 className={`text-xl font-bold ${textPrimary}`}>Top Recommended Investors</h2>
                <Link to="/discovery">
                  <Button className={`border ${isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`} variant="outline" data-testid="view-all-investors">View All</Button>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recommendations.slice(0, 6).map(rec => (
                  <Link key={rec.user_id} to={`/profile/${rec.user_id}`}>
                    <Card className={`p-4 cursor-pointer transition-all ${isDark ? 'bg-slate-600 border-slate-500 hover:border-blue-400' : 'bg-slate-50 border-slate-200 hover:border-blue-500'} hover:shadow-md`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className={`font-semibold ${textPrimary}`}>{rec.name}</h3>
                          <p className={`text-sm ${textMuted}`}>{rec.profile.investor_type}</p>
                        </div>
                        <span className="match-score-badge">{rec.match_score}%</span>
                      </div>
                      <p className={`text-xs mb-2 ${textSecondary}`}>{rec.profile.city}, {rec.profile.country}</p>
                      <div className="flex flex-wrap gap-1">
                        {rec.profile.preferred_industries.slice(0, 3).map(ind => (
                          <span key={ind} className={`text-xs px-2 py-1 rounded ${badgeBg}`}>
                            {ind}
                          </span>
                        ))}
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>

      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        onClose={closeUpgradeModal}
        feature={upgradeModal.feature}
      />

      {/* Upgrade Modal for tier-gated features */}
      <QuotaUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
        userRole={user?.role}
        quotaType="pro_feature"
      />
    </div>
  );
}
